import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
  clickable: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly breadcrumbsSignal = signal<Breadcrumb[]>([]);
  private readonly labelOverrides = signal<Record<string, string>>({});

  readonly breadcrumbs = computed(() =>
    this.breadcrumbsSignal().map((crumb) => ({
      ...crumb,
      label: this.labelOverrides()[crumb.url] ?? crumb.label,
    }))
  );

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const root = this.activatedRoute.root;
        const breadcrumbs = this.createBreadcrumbs(root);
        this.breadcrumbsSignal.set(breadcrumbs);
      });
  }

  setLabelOverride(url: string, label: string): void {
    this.labelOverrides.update((o) => ({ ...o, [url]: label }));
  }

  clearLabelOverride(url: string): void {
    this.labelOverrides.update((o) => {
      const copy = { ...o };
      delete copy[url];
      return copy;
    });
  }

  private createBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data['breadcrumb'];
      if (label) {
        const clickable = child.snapshot.data['breadcrumbLink'] !== false;
        breadcrumbs.push({ label, url, clickable });
      }

      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }
}
